import raw from "../../../data/seed/isolatieglas.json";
import type { IsoMarginKey } from "@/lib/settings";

/** Pakket-sleutel → marge-variant (standaard/basis/premium/sun/triple) voor de per-variant marge. */
export function isoVariantForPakket(pakket: string): IsoMarginKey {
  if (pakket === "triple") return "triple";
  if (pakket === "hr-1.1") return "basis";
  if (pakket === "hr-1.0") return "premium";
  if (pakket.startsWith("zonwerend")) return "sun";
  return "standaard";
}

/** Eén complete isolatieglas-opbouw (buitenruit + spouw/pakket + binnenruit) uit de prijslijst-Excel. */
export interface Buildup {
  typ: "hr" | "standaard" | "zonwerend" | "triple";
  pakket: string;
  uValue: number | null;
  /** Lichttoetreding % (zonwerend), bv. 40 bij Klima 400. */
  lta: number | null;
  /** Zontoetreding % (zonwerend), bv. 21 bij Klima 400. */
  zta: number | null;
  gLabel: string | null;
  klima: string | null;
  outer: string;
  middle: string | null;
  inner: string;
  /** Positie van de low-e coating (de * in de prijslijst): spouwzijde van die ruit. */
  coating: "outer" | "inner" | "both" | null;
  art: string;
  pricePerM2: number; // netto inkoop, cents
  /** Lijst-verkoopprijs (adviesprijs), cents — ter referentie. */
  advCents: number;
  minAreaM2: number;
}

export const BUILDUPS = (raw as { buildups: Buildup[] }).buildups;

/** Laagste isolatieglas-verkoopprijs (€/m² excl. btw, cents) over álle opbouwen — de "vanaf"-prijs
 *  van de isolatieglas-landingspagina. Elke opbouw met z'n eigen variant-marge. */
export function isoMinVanafCents(margins: Record<IsoMarginKey, number>): number | null {
  const prices = BUILDUPS.map((b) => {
    const m = margins[isoVariantForPakket(b.pakket)] ?? 0;
    return Math.round(b.pricePerM2 * (1 + (m > 0 ? m : 0) / 100));
  });
  return prices.length ? Math.min(...prices) : null;
}
export const MIN_AREA_M2 = 0.65;

/** Een isolatie-pakket = de "spouw"-keuze: bepaalt coating/gas/U-waarde. */
export interface Pakket {
  key: string;
  label: string;
  sub: string;
  uValue: number | null;
  gLabel: string | null;
}

const PAKKET_META: Record<string, { label: string; sub: string }> = {
  "hr-1.0": { label: "HR++ · U 1,0", sub: "Beste warmte-isolatie (U 1,0 W/m²K)" },
  "hr-1.1": { label: "HR++ · U 1,1", sub: "Warmte-isolatie (U 1,1 W/m²K)" },
  standaard: { label: "Standaard dubbelglas", sub: "Dubbel glas zonder coating" },
  triple: { label: "Triple glas", sub: "3 ruiten, topisolatie" },
};
const klimaG: Record<string, string> = { "400": "40/21", "500": "51/26", "600": "64/32", "700": "68/37", "740": "70/38" };

/** U-waarde per pakket: alléén HR (1.0/1.1) staat exact in de productdata. Voor standaard dubbel,
 *  triple en zonwerend staat de U-waarde NIET in de prijslijst (hangt van spouw/gas/coating af) →
 *  null = onbekend, in de UI getoond als "?" i.p.v. een verzonnen getal. */
function pakketU(_pakket: string, uValue: number | null): number | null {
  return uValue;
}

/** Alle pakketten die in de data voorkomen, in een logische volgorde. */
export function pakketten(): Pakket[] {
  const order = ["hr-1.0", "hr-1.1", "triple", "standaard", "zonwerend-740", "zonwerend-700", "zonwerend-600", "zonwerend-500", "zonwerend-400"];
  const present = [...new Set(BUILDUPS.map((b) => b.pakket))];
  present.sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99));
  return present.map((key) => {
    const ex = BUILDUPS.find((b) => b.pakket === key)!;
    const klima = key.startsWith("zonwerend") ? key.split("-")[1]! : null;
    return {
      key,
      label: klima ? `Zonwerend · Klima ${klima}` : (PAKKET_META[key]?.label ?? key),
      sub: klima ? `Zonwering, lt/zta ${klimaG[klima] ?? "?"}` : (PAKKET_META[key]?.sub ?? ""),
      uValue: pakketU(key, ex.uValue),
      gLabel: klima ? (klimaG[klima] ?? null) : null,
    };
  });
}

/** Buitenruit-opties voor een pakket. */
export function outersFor(pakket: string): string[] {
  return [...new Set(BUILDUPS.filter((b) => b.pakket === pakket).map((b) => b.outer))].sort(paneSort);
}
/** Binnenruit-opties voor een pakket + buitenruit. */
export function innersFor(pakket: string, outer: string): string[] {
  return [...new Set(BUILDUPS.filter((b) => b.pakket === pakket && b.outer === outer).map((b) => b.inner))].sort(paneSort);
}
export function findBuildup(pakket: string, outer: string, inner: string, middle?: string): Buildup | undefined {
  return BUILDUPS.find((b) => b.pakket === pakket && b.outer === outer && b.inner === inner && (middle === undefined || b.middle === middle));
}

// --- Triple (3 ruiten: buiten · midden · binnen) -----------------------------------------------
export function tripleOuters(): string[] {
  return [...new Set(BUILDUPS.filter((b) => b.typ === "triple").map((b) => b.outer))].sort(paneSort);
}
export function tripleMiddles(outer: string): string[] {
  return [...new Set(BUILDUPS.filter((b) => b.typ === "triple" && b.outer === outer && b.middle).map((b) => b.middle!))].sort(paneSort);
}
export function tripleInners(outer: string, middle: string): string[] {
  return [...new Set(BUILDUPS.filter((b) => b.typ === "triple" && b.outer === outer && b.middle === middle).map((b) => b.inner))].sort(paneSort);
}


/** 1 folie (PVB) = 0,38 mm — telt mee in de exacte dikte van een gelaagde ruit. */
export const FOLIE_MM = 0.38;

/** Exacte dikte (mm) van een ruit-token, incl. folie. Gelaagd 33.1 = 3+3 glas + 1×0,38 folie =
 *  6,38 mm; 44.2 = 8,76 mm. Massief float = de nominale dikte. Op 2 decimalen. Suffixen (mat/sc/…)
 *  veranderen de dikte niet. */
export function paneThicknessMm(token: string): number {
  const m = token.match(/^(\d{1,4})(?:\.(\d))?/);
  if (!m) return 0;
  const base = m[1]!;
  if (m[2] == null) return Number(base); // massief float
  // Gelaagd XY.N: glasbladen = de cijfers (33 → 3+3, 88 → 8+8, 1010 → 10+10); N = aantal folies.
  const glass = base === "1010" ? 20 : base === "1212" ? 24 : base.split("").reduce((s, c) => s + Number(c), 0);
  return Math.round((glass + Number(m[2]) * FOLIE_MM) * 100) / 100;
}

/** Nette suffix-teksten voor de afwerking-tokens uit de prijslijst (mat, SC, figuur, …). */
const FINISH_LABEL: Record<string, string> = {
  "": "",
  mat: " mat",           // gelaagd: matte folie → "mat"; massief float: hieronder "acide"
  sc: " geluidwerend (Sound-Control)",
  p4a: " inbraakwerend (P4A)",
  acide: " acide (mat geëtst)",
  canale: " canalé (figuurglas)",
  canalemb: " canalé mat/blank (figuurglas)",
  byzantijn: " byzantijn (figuurglas)",
  kathgroot: " kathedraal groot (figuurglas)",
  kathklein: " kathedraal klein (figuurglas)",
  gehard: " gehard",
  grijsgeh: " grijs gehard",
  // Sinds de prijslijst van 11-09-2026.
  bronsgeh: " brons gehard",
  crepi: " crepi (figuurglas)",
  waterdrup: " waterdruppel (figuurglas)",
  silvit: " silvit (figuurglas)",
};

/** Figuurglas-afwerkingen: één lijst, zodat tekening en categorie-knop nooit uiteenlopen. */
const FIGUUR_FINISHES = ["byzantijn", "canale", "canalemb", "kathgroot", "kathklein", "crepi", "waterdrup", "silvit"];
const GEHARD_FINISHES = ["gehard", "grijsgeh", "bronsgeh"];

/** Splitst een ruit-token in het numerieke deel (base) en het afwerking-suffix. Het suffix mag
 *  zelf cijfers bevatten (bv. "p4a") mits het met een letter begint. */
function splitToken(token: string): { base: string; fin: string; lam: boolean } {
  const m = token.match(/^(\d{1,4}(?:\.\d)?)([a-z][a-z0-9]*)?$/i);
  const base = m?.[1] ?? token;
  return { base, fin: (m?.[2] ?? "").toLowerCase(), lam: base.includes(".") };
}

/** Aantal folies in een gelaagde ruit = het cijfer achter de punt (33.1 = 1, 44.2 = 2). 1 folie = 0,38 mm. */
export function paneFolieCount(token: string): number {
  const m = token.match(/^\d{1,4}\.(\d)/);
  return m ? Number(m[1]) : 0;
}

/** Visuele afwerking-categorie van een ruit — voor de symbolen/rasters in de tekening. */
export type PaneFinish = "blank" | "mat" | "figuur" | "gehard" | "geluidwerend" | "inbraakwerend";
export function paneFinish(token: string): PaneFinish {
  const { fin } = splitToken(token);
  if (fin === "sc") return "geluidwerend";
  if (fin === "p4a") return "inbraakwerend";
  if (GEHARD_FINISHES.includes(fin)) return "gehard";
  if (FIGUUR_FINISHES.includes(fin)) return "figuur";
  if (fin === "mat" || fin === "acide") return "mat";
  return "blank";
}

/** Categorie(ën) van een ruit → categorie-knop(pen). Leeg = blank float → gewone dikte-knop.
 *  Een ruit kan onder meerdere categorieën vallen: gelaagd mat (33.1mat) staat zowel onder
 *  "Gelaagd" als onder "Mat"; gelaagd geluidwerend (44.2sc) onder "Gelaagd" én "Geluidwerend".
 *  Gelaagde diktes (33.1 ≈ 6,4 mm) staan bewust niet tussen de mm-knoppen. */
export function paneCategories(token: string): string[] {
  const { fin, lam } = splitToken(token);
  const cats: string[] = [];
  if (FIGUUR_FINISHES.includes(fin)) cats.push("figuur");
  if (fin === "sc") cats.push("geluidwerend");
  if (fin === "p4a") cats.push("inbraakwerend");
  if (GEHARD_FINISHES.includes(fin)) cats.push("gehard");
  if (fin === "mat" || fin === "acide") cats.push("mat");
  // Geluidwerend (sc) en inbraakwerend (p4a) zijn constructief óók gelaagd, maar we tonen ze
  // alléén onder hun eigen (functionele) categorie — anders levert "Gelaagd" kiezen een ruit
  // op met een geluidwerend-/inbraakwerend-symbool. Mat/blank gelaagd blijft wél onder Gelaagd
  // (en mat-gelaagd staat bewust onder Mat én Gelaagd).
  if (lam && fin !== "sc" && fin !== "p4a") cats.push("gelaagd");
  return cats;
}

const CATEGORY_ORDER = ["gelaagd", "mat", "figuur", "geluidwerend", "gehard", "inbraakwerend"];
const CATEGORY_LABEL: Record<string, string> = {
  gelaagd: "Gelaagd", mat: "Mat", figuur: "Figuurglas", geluidwerend: "Geluidwerend", gehard: "Gehard", inbraakwerend: "Inbraakwerend",
};

export interface PaneCategory { key: string; label: string; items: { token: string; label: string }[] }
export interface PaneOptions { plains: { mm: number; token: string }[]; categories: PaneCategory[] }

/** Splitst ruit-tokens in echte float-diktes (mm-knoppen) + categorie-knoppen die uitklappen. */
export function paneOptions(tokens: string[]): PaneOptions {
  const plains: { mm: number; token: string }[] = [];
  const catMap = new Map<string, { token: string; label: string }[]>();
  for (const t of tokens) {
    const cats = paneCategories(t);
    if (cats.length === 0) plains.push({ mm: paneThicknessMm(t), token: t });
    else for (const cat of cats) (catMap.get(cat) ?? catMap.set(cat, []).get(cat)!).push({ token: t, label: paneLabel(t) });
  }
  plains.sort((a, b) => a.mm - b.mm);
  const categories = CATEGORY_ORDER.filter((k) => catMap.has(k)).map((k) => ({
    key: k, label: CATEGORY_LABEL[k]!,
    items: catMap.get(k)!.sort((a, b) => paneThicknessMm(a.token) - paneThicknessMm(b.token) || a.token.localeCompare(b.token)),
  }));
  return { plains, categories };
}

/** Nummer met NL-komma, max 2 decimalen (6,38 · 15 · 8,76). */
export const fmtMm = (v: number): string => `${Math.round(v * 100) / 100}`.replace(".", ",");

/** Klant-label voor een ruit-token, bv. "33.1mat" → "Gelaagd 33.1 mat (6,38 mm)". Massief mat glas
 *  heet acide (mat geëtst); alleen bij een gelaagde ruit is "mat" de matte folie. */
export function paneLabel(token: string): string {
  const m = token.match(/^(\d{1,4}(?:\.\d)?)([a-z][a-z0-9]*)?$/i);
  if (!m) return token;
  const base = m[1]!;
  const lam = base.includes(".");
  let fin = (m[2] ?? "").toLowerCase();
  if (fin === "mat" && !lam) fin = "acide"; // massief float: "mat" = acide-geëtst
  const suffix = FINISH_LABEL[fin] ?? (fin ? ` ${fin}` : "");
  return lam
    ? `Gelaagd ${base}${suffix} (${fmtMm(paneThicknessMm(token))} mm)`
    : `${base} mm${suffix}`;
}

const paneSort = (a: string, b: string): number => paneThicknessMm(a) - paneThicknessMm(b) || a.localeCompare(b);

// --- Uitvoering (eerste stap) → pakket-mapping ---------------------------------------------------
export interface Uitvoering { key: string; label: string; sub: string }
export const UITVOERINGEN: Uitvoering[] = [
  { key: "standaard", label: "Standaard dubbelglas", sub: "Dubbel glas zonder coating" },
  { key: "basis", label: "Hoogrendementsglas BASIS", sub: "HR++ · U 1,1 W/m²K" },
  { key: "premium", label: "Hoogrendementsglas PREMIUM", sub: "HR++ · U 1,0 W/m²K" },
  { key: "sun", label: "Hoogrendementsglas SUN", sub: "Zonwerend (Klima)" },
];

/** Uitvoering (+ Klima-niveau bij SUN) → pakket-sleutel in de data. */
export function pakketFor(uitvoering: string, klima?: string): string {
  if (uitvoering === "basis") return "hr-1.1";
  if (uitvoering === "premium") return "hr-1.0";
  if (uitvoering === "sun") return `zonwerend-${klima ?? klimaOptions()[0]?.klima ?? "700"}`;
  return "standaard";
}

/** Beschikbare Klima-niveaus (zonwerend) met g/lt-label, oplopend. */
export function klimaOptions(): { klima: string; g: string | null }[] {
  const g: Record<string, string> = { "400": "40/21", "500": "51/26", "600": "64/32", "700": "68/37", "740": "70/38" };
  return [...new Set(BUILDUPS.filter((b) => b.typ === "zonwerend").map((b) => b.klima!))]
    .sort()
    .map((klima) => ({ klima, g: g[klima] ?? null }));
}

// --- Vorm (zelfde geometrie als enkel glas, EIGEN toeslagen voor isolatieglas) -----------------
export interface Vorm { id: string; name: string; surchargePct: number }
// Volledige enkelglas-vormenset (ids = shape-meta.ts / getShapeMeta), maar isolatieglas
// (dubbel + triple, ook TripleConfigurator gebruikt deze lijst) heeft EIGEN toeslag-percentages
// die afwijken van de Timmers-percentages voor enkel glas (data/seed/products.json) — vastgelegd
// door Thijs, 31-07-2026: 1 schuine zijde 80%, 2 schuine zijden 80%, toog 170%, cirkel 210%.
// De eerste 7 (COMMON_VORM_IDS) staan direct; de rest onder "Meer vormen". Zelfde geometrie als
// enkelglas via IsolatieglasShapePreview → getShapeMeta.
export const VORMEN: Vorm[] = [
  { id: "rechthoek", name: "Rechthoek", surchargePct: 0 },
  { id: "schuine-zijde", name: "1 schuine zijde", surchargePct: 0.8 },
  { id: "schuine-zijden-2", name: "2 schuine zijden", surchargePct: 0.8 },
  { id: "toog", name: "Toog", surchargePct: 1.7 },
  { id: "cirkel", name: "Cirkel", surchargePct: 2.1 },
  { id: "speciaal", name: "Speciale vorm", surchargePct: 0.8 },
  { id: "driehoek", name: "Driehoek", surchargePct: 0.8 },
  { id: "kwartcirkel", name: "Kwart cirkel", surchargePct: 0.8 },
  { id: "parallelogram", name: "Parallellogram", surchargePct: 0.8 },
  { id: "rechthoekige-driehoek", name: "Rechthoekige driehoek", surchargePct: 0.4 },
  { id: "trapezium", name: "Trapezium", surchargePct: 0.8 },
];
/** Vormen die direct getoond worden; de rest valt onder de "Meer vormen"-uitklapper. */
export const COMMON_VORM_IDS: readonly string[] = [
  "rechthoek", "schuine-zijde", "schuine-zijden-2", "toog", "cirkel", "speciaal",
];
export const vormSurcharge = (id: string): number => VORMEN.find((v) => v.id === id)?.surchargePct ?? 0;

// --- Spouw + afstandhouder ---------------------------------------------------------------------
export const SPOUW_GASSEN = [
  { id: "argon", label: "Argon (standaard)" },
  { id: "krypton", label: "Krypton" },
  { id: "lucht", label: "Lucht" },
];

/**
 * Afstandhouders (Timmers, navraag juli 2026). Het type bepaalt óók welke spouwbreedtes er
 * gemaakt kunnen worden — vandaar dat de configurator eerst het type vraagt en pas daarna de
 * breedte. De toeslag is netto inkoop per strekkende meter OMTREK; bij triple ligt er een tweede
 * afstandhouder in, dus daar telt het tarief dubbel.
 */
export interface SpacerType {
  id: string;
  label: string;
  /** Eén regel die de klant vertelt wanneer je dit kiest. */
  kort: string;
  /** De uitleg zoals Timmers 'm geeft — in de info-uitklap. */
  uitleg: string;
  hex: string;
  /** Netto inkoop per m1 omtrek (cents); 0 = standaard, geen toeslag. */
  perM1Cents: number;
  spouwMm: number[];
}

/** Waarom je deze afwerking zou kiezen — ook los gebruikt in de uitleg-uitklapper. */
export const SILICONEN_UITLEG =
  "Siliconen randafdichting (UV-bestendig) is een speciale zwarte kitrand die tegen ultraviolet licht kan. Nodig bij ruiten waarvan de rand blootstaat aan zon en vocht — lichtstraten, tuinkamers, bootramen — en bij elke ruit die niet rondom met latten of profielen is afgedekt. Zit de ruit rondom achter glaslatten, dan heeft het geen toegevoegde waarde.";

export const SPACER_TYPES: readonly SpacerType[] = [
  {
    id: "alu",
    label: "Standaard aluminium",
    kort: "Aluminiumkleurig · standaard, geen meerprijs",
    uitleg:
      "De gebruikelijke aluminium afstandhouder. Prima keuze voor ruiten die achter glaslatten of in een kozijn zitten.",
    hex: "#c7ccd1",
    perM1Cents: 0,
    spouwMm: [6, 8, 10, 12, 13, 14, 15, 16, 18, 20],
  },
  {
    id: "tgi",
    label: "Warm Edge TGI (zwart)",
    kort: "Thermisch onderbroken · minder warmteverlies langs de rand",
    uitleg:
      "De TGI®-Spacer is een thermisch onderbroken afstandhouder die het warmteverlies aan de rand van het isolatieglas vermindert en de isolatiewaarde van het hele raam (glas + kozijn) verbetert. Minder koudebrug langs de rand betekent ook minder kans op condens op de randzone.",
    hex: "#3a3f47",
    // € 2,75/m1 volgens de opdrachtbevestiging van Timmers bij order 1023 (02-09-2026). Stond
    // op € 2,00 na navraag in juli 2026; dat tarief is sindsdien gewijzigd.
    perM1Cents: 275,
    spouwMm: [8, 10, 12, 14, 15, 16, 18, 20],
  },
  {
    id: "sil",
    label: "UV-bestendige siliconen afdichting (zwart)",
    kort: "Voor randen in zon en vocht — lichtstraat, tuinkamer, boot",
    uitleg: SILICONEN_UITLEG,
    hex: "#1b1d21",
    perM1Cents: 950,
    spouwMm: [8, 10, 12, 14, 15, 16, 18, 20],
  },
];

export const spacerType = (id: string | undefined): SpacerType => SPACER_TYPES.find((s) => s.id === id) ?? SPACER_TYPES[0]!;
export const spacerHex = (id: string): string => spacerType(id).hex;
/** Spouwbreedtes die bij dit type gemaakt kunnen worden. */
export const spouwOptiesVoor = (spacerId: string | undefined): number[] => spacerType(spacerId).spouwMm;
/** Dichtstbijzijnde toegestane spouw als de huidige keuze bij dit type niet bestaat. */
export function spouwBinnenType(spacerId: string | undefined, spouwMm: number): number {
  const opties = spouwOptiesVoor(spacerId);
  if (opties.includes(spouwMm)) return spouwMm;
  return opties.reduce((best, mm) => (Math.abs(mm - spouwMm) < Math.abs(best - spouwMm) ? mm : best), opties[0]!);
}

/**
 * Bewaard voor bestaande orders: vóór 27-07-2026 was de siliconen-randafdichting een los vinkje
 * bovenop de afstandhouder. Nu is het de dérde afstandhouder-keuze (id "sil"), want de rand wordt
 * dan zwart afgewerkt — het is geen extraatje maar een andere uitvoering.
 */
export const SILICONEN_PER_M1_CENTS = 950;
